import { messages } from "../../../src/resources/strings";
import { updatePolicy, toogleLoading } from "../../store/slices/PolicySlice";
import { useAppDispatch } from "../../store/Store";
import Constants, { getCompanyName } from "../../utilities/Constants";
import Storage from "../../utilities/storage/StorageFunctions";
import { usePolicies } from "../policies";
import { hitGrantAccessApiOnGetPolicy401Error } from "../user";
import { getPolicyNumber } from "../../utilities/Helpers";
import { updatePolicyDetails } from "../../store/slices/PolicyDetailSlice";

// Grant Retry Limit
const RETRY_LIMIT = 2;

const policyApiHandler = (setErrorMessage: (data: string) => void) => {
  const dispatch = useAppDispatch();
  const { getPolicies, getDiscoverPolicies, getPolicyDetail } = usePolicies();

  //   Retry counter functions
  const getRetryCounter = async (): Promise<number> => {
    const counter = await Storage.getData(
      Constants.LocalStoreConstant.GRANT_ACCESS_API_COUNTER
    );
    return parseInt(counter || "0");
  };

  const setRetryCounter = async (val: number) => {
    await Storage.storeData(
      Constants.LocalStoreConstant.GRANT_ACCESS_API_COUNTER,
      val.toString()
    );
  };

  const clearRetryCounter = async () => {
    await Storage.clearData(
      Constants.LocalStoreConstant.GRANT_ACCESS_API_COUNTER
    );
  };

  const setLoading = (status: boolean) => {
    dispatch(toogleLoading(status));
  };
  //   Api error handlers
  const handleDiscoveryPolicyError = (
    policiesResponse: any, // discover api
    policyLength: number // listing api
  ) => {
    if (policiesResponse.errorCode === messages.error.noPolicyFound) {
      if (policyLength === 0) setErrorMessage(messages.error.noPolicyFound);
    } else {
      setErrorMessage(policiesResponse.message || messages.error.somthingWrong);
    }
  };

  const handleSingleError = (policiesResponse: any) => {
    if (policiesResponse.errorCode === messages.error.noPolicyFound) {
      setErrorMessage(messages.error.noPolicyFound);
    } else {
      setErrorMessage(policiesResponse.message || messages.error.somthingWrong);
    }
  };

  //   grant user access and retry fetch policies
  const handleHarmonyUserError = async (retryFn: () => void) => {
    const currentCount = await getRetryCounter();
    if (currentCount >= RETRY_LIMIT) {
      await clearRetryCounter();
      setErrorMessage(messages.error.noHarmonyUserFound);
    }

    try {
      await hitGrantAccessApiOnGetPolicy401Error(currentCount);
      const newCount = currentCount + 1;
      await setRetryCounter(newCount);

      if (newCount < RETRY_LIMIT) {
        const delay = Math.pow(2, currentCount) * 1000;
        setTimeout(retryFn, delay);
      }
    } catch {
      setErrorMessage(messages.error.noHarmonyUserFound);
    }
  };

  //   fetch discover policies
  const fetchDiscoverPolicies = async (policyLength: number) => {
    try {
      const discoveryResponse = await getDiscoverPolicies();
      if (discoveryResponse.errorCode === messages.error.noError) {
        await dispatch(updatePolicy(discoveryResponse?.response || []));
      } else {
        handleDiscoveryPolicyError(discoveryResponse, policyLength);
      }
    } catch (error: any) {
      setLoading(false);
      setErrorMessage(error.message || error.toString());
    }
  };
  //   fetch policy listing
  const fetchUserPolicies = async (): Promise<any> => {
    try {
      const policyListResponse = await getPolicies();
      if (
        policyListResponse.errorCode === messages.error.noError &&
        policyListResponse?.response?.length > 0
      ) {
        await clearRetryCounter();
        await dispatch(updatePolicy(policyListResponse?.response || []));
        setLoading(false);
        return policyListResponse?.response || [];
      }

      if (
        policyListResponse.errorCode === messages.error.noHarmonyUserFound &&
        policyListResponse?.message === "User not authorized for Harmony"
      ) {
        await handleHarmonyUserError(fetchUserPolicies);
        return [];
      } else {
        handleSingleError(policyListResponse);
        return [];
      }
    } catch (error) {
      setLoading(false);
      console.error("Fetch user policies error:", error);
    }
  };

  //   fetchAllPolicies -> listing -> discovery
  const fetchAllPolicies = async () => {
    try {
      setLoading(true);
      const userPolicies = await fetchUserPolicies();
      const companyName = await getCompanyName();

      if (companyName !== "HCPCI") {
        await fetchDiscoverPolicies(userPolicies.length);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setErrorMessage(error.message || messages.error.somthingWrong);
    }
  };

  const refetchPolicyDetailData = async () => {
    setLoading(true);
    const policyNumber = await getPolicyNumber();
    const response = await getPolicyDetail(policyNumber);
    if (response.errorCode === messages.error.noError) {
      dispatch(updatePolicyDetails(response.response));
    } else {
      setErrorMessage(response.errorCode || messages.error.somthingWrong);
    }
    setLoading(false);
  };

  return {
    fetchAllPolicies,
    refetchPolicyDetailData,
  };
};

export default policyApiHandler;
